import { memo, useCallback, useMemo, useState } from 'react';

import {
  BadgeCell,
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Col,
  Combobox,
  DateCell,
  Datepicker,
  dateTimeFormatter,
  formattedCell,
  type HookFormMethods,
  HookFormResolver,
  PageTitle,
  Row,
  Text
} from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import { ActionsGrid } from 'app/components/grid';
import { type ActionColumn } from 'app/components/grid/actions-column';
import GridApplication from 'app/components/grid/gridApplication';
import { type CatalogModel } from 'app/modules/Parameters/Catalog/Catalog/models/types';
import { catalogs } from 'app/modules/Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from 'app/modules/Parameters/Catalog/Catalog/services/useCatalog';
import { type TaxReportGeneralInfo } from 'app/modules/Reports/types';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface SearchCatalogType {
  dateFrom?: Date | null;
  dateTo?: Date | null;
  uploadNumber?: string;
  taxCollector?: CatalogModel | null;
  taxCollectorId?: string | number;
}

type SearchParams = SearchCatalogType & {
  relations: Array<string>;
};

const relations = ['TaxCollector'];

const initialSearch: SearchParams = {
  relations
};

export const ViewBankInformationPage = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState<SearchParams>(initialSearch);

  const [{ data: taxCollectors, loading: loadingTaxCollector, error: errorTaxCollector }] =
    useCatalogList(catalogs.taxCollector);

  const onSearch = useCallback((values: SearchCatalogType) => {
    const taxCollectorFilter = values.taxCollector
      ? { taxCollectorId: values.taxCollector.id }
      : {};

    setSearch({
      ...values,
      ...taxCollectorFilter,
      relations
    });
  }, []);

  const actions = useMemo<Array<ActionColumn>>(
    () => [
      {
        icon: Icon.Search,
        variant: 'secondary',
        onClick: (row: TaxReportGeneralInfo) => {
          navigate(`/taxpayer/check-load-records/${row.id}`);
        }
      }
    ],
    [navigate]
  );

  const colDef = useMemo<Array<ColDef>>(
    () => [
      {
        headerName: t('common.label.actions'),
        cellRenderer: ActionsGrid,
        cellRendererParams: {
          actions
        }
      },
      {
        headerName: t('taxpayer-payments.label.uploadNumber'),
        field: 'uploadNumber'
      },
      {
        headerName: t('taxpayer-payments.label.taxCollector'),
        field: 'taxCollector',
        valueFormatter: (params: ValueFormatterParams) => params.value?.name ?? ''
      },
      {
        headerName: t('report-details.label.taxReportStatus'),
        field: 'status',
        ...formattedCell(
          (params: ValueFormatterParams): string => {
            return t(`payment-file-status.${params.value}`);
          },
          BadgeCell((params) => {
            switch (params.value) {
              case 'received':
                return 'success';
              case 'pending':
                return 'secondary';
              default:
                return 'danger';
            }
          })
        )
      },
      {
        headerName: t('report-details.label.registrationDate'),
        field: 'createdDate',
        ...formattedCell(dateTimeFormatter, DateCell)
      }
    ],
    [actions, t]
  );

  const handleReset = useCallback((reset: HookFormMethods['reset']) => {
    setSearch(initialSearch);

    reset({
      taxCollector: null,
      uploadNumber: '',
      dateFrom: null,
      dateTo: null
    });
  }, []);

  if (loadingTaxCollector || errorTaxCollector) {
    return null;
  }

  return (
    <>
      <PageTitle title={t('taxpayer-payments.title.checkBankInformation')} icon={Icon.Bank2} />

      <HookFormResolver onSubmit={onSearch}>
        {({ reset }: HookFormMethods) => (
          <>
            <Row>
              <Col>
                <Combobox
                  code="id"
                  display="name"
                  data={taxCollectors}
                  label={t('taxpayer-payments.label.taxCollector')}
                  name="taxCollector"
                />
              </Col>

              <Col>
                <Text label={t('taxpayer-payments.label.uploadNumber')} name="uploadNumber" />
              </Col>
            </Row>

            <Row>
              <Col>
                <Datepicker label={t('common.label.dateFrom')} name="dateFrom" />
              </Col>

              <Col>
                <Datepicker label={t('common.label.dateTo')} name="dateTo" />
              </Col>
            </Row>

            <BtnToolbar>
              <BtnGroup>
                <Button
                  action={ButtonAction.clean}
                  variant="secondary"
                  type="reset"
                  onClick={() => handleReset(reset)}
                />
              </BtnGroup>

              <BtnGroup>
                <Button action={ButtonAction.find} type="submit" variant="primary" />
              </BtnGroup>
            </BtnToolbar>
          </>
        )}
      </HookFormResolver>
      <hr />
      <Row>
        <Col>
          <GridApplication url="/payments/file" columnDefs={colDef} search={search} />
        </Col>
      </Row>
    </>
  );
});

ViewBankInformationPage.displayName = 'ViewBankInformationPage';
